import axios, { AxiosError } from 'axios';
import { ENV } from '../../env';
import { CHATBOT_ENDPOINTS } from '../api/endpoints';
import {
  ChatbotHealthResponse,
  ChatbotReadyResponse,
  ChatRequest,
  ChatResponseEnvelope,
  WsChatEvent,
  WsChatQueryMessage,
} from '../../types/api/ecpt/chatbot';

const chatbotClient = axios.create({
  baseURL: ENV.CHATBOT_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 45_000,
});

export class ChatbotApiError extends Error {
  status?: number;
  code?: string;
  detail?: string;

  constructor(message: string, status?: number, code?: string, detail?: string) {
    super(message);
    this.name = 'ChatbotApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

export interface WsChatCallbacks {
  onStatus?: (status: string, message?: string) => void;
  onToken?: (token: string) => void;
  onResult?: (envelope: ChatResponseEnvelope) => void;
  onError?: (error: ChatbotApiError) => void;
  onDone?: () => void;
}

export const ecptChatbotService = {
  /**
   * Check if backend service is running.
   * GET /api/health
   */
  async getHealth(): Promise<ChatbotHealthResponse> {
    try {
      const response = await chatbotClient.get<ChatbotHealthResponse>(CHATBOT_ENDPOINTS.HEALTH);
      return response.data;
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; detail?: string }>;
      throw new ChatbotApiError(
        axiosErr.response?.data?.message || axiosErr.message || 'Failed to check chatbot health',
        axiosErr.response?.status,
        'HEALTH_CHECK_FAILED',
        axiosErr.response?.data?.detail,
      );
    }
  },

  /**
   * Check PostgreSQL and Redis readiness before running chat queries.
   * GET /api/ready
   */
  async getReadiness(): Promise<ChatbotReadyResponse> {
    try {
      const response = await chatbotClient.get<ChatbotReadyResponse>(CHATBOT_ENDPOINTS.READY);
      return response.data;
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; detail?: string }>;
      throw new ChatbotApiError(
        axiosErr.response?.data?.message || axiosErr.message || 'Failed to check chatbot readiness',
        axiosErr.response?.status,
        'READY_CHECK_FAILED',
        axiosErr.response?.data?.detail,
      );
    }
  },

  /**
   * Send chat message via REST API.
   * POST /api/chat
   */
  async sendChatMessage(payload: ChatRequest): Promise<ChatResponseEnvelope> {
    try {
      const requestBody: ChatRequest = {
        message: payload.message,
        app_context: payload.app_context || 'ecpt',
        language: payload.language || 'en',
        ...(payload.conversation_id ? { conversation_id: payload.conversation_id } : {}),
        ...(payload.resume_token ? { resume_token: payload.resume_token } : {}),
      };

      const response = await chatbotClient.post<ChatResponseEnvelope>(
        CHATBOT_ENDPOINTS.CHAT,
        requestBody,
      );
      return response.data;
    } catch (err) {
      const axiosErr = err as AxiosError<{ detail?: string; message?: string; error?: string }>;

      if (axiosErr.response?.status === 429) {
        throw new ChatbotApiError(
          'Rate limit exceeded. Please wait a moment before sending another message.',
          429,
          'RATE_LIMITED',
          axiosErr.response.data?.detail,
        );
      }

      if (axiosErr.response?.status === 422) {
        throw new ChatbotApiError(
          axiosErr.response.data?.detail ||
            axiosErr.response.data?.message ||
            'Invalid request parameters. Please verify your input.',
          422,
          'VALIDATION_ERROR',
          axiosErr.response.data?.detail,
        );
      }

      const message =
        axiosErr.response?.data?.message ||
        axiosErr.response?.data?.detail ||
        axiosErr.response?.data?.error ||
        axiosErr.message ||
        'An error occurred while communicating with the assistant.';

      throw new ChatbotApiError(
        message,
        axiosErr.response?.status,
        'CHAT_REQUEST_FAILED',
        axiosErr.response?.data?.detail,
      );
    }
  },

  /**
   * Open a WebSocket chat session to stream tokens and progress updates.
   * WS /ws/chat
   */
  connectWebSocketChat(payload: ChatRequest, callbacks: WsChatCallbacks): () => void {
    let ws: WebSocket | null = null;
    let isClosedManually = false;

    try {
      let wsUrl = ENV.CHATBOT_WS_URL;
      if (!wsUrl.startsWith('ws://') && !wsUrl.startsWith('wss://')) {
        const parsed = new URL(ENV.CHATBOT_API_URL);
        const protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${protocol}//${parsed.host}${CHATBOT_ENDPOINTS.WS_CHAT}`;
      }

      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        const queryMessage: WsChatQueryMessage = {
          type: 'query',
          message: payload.message,
          app_context: payload.app_context || 'ecpt',
          language: payload.language || 'en',
          ...(payload.conversation_id ? { conversation_id: payload.conversation_id } : {}),
          ...(payload.resume_token ? { resume_token: payload.resume_token } : {}),
        };
        ws?.send(JSON.stringify(queryMessage));
      };

      ws.onmessage = (event) => {
        try {
          const data: WsChatEvent = JSON.parse(event.data);

          switch (data.type) {
            case 'status':
              callbacks.onStatus?.(data.status || 'processing', data.message);
              break;
            case 'token':
              if (data.token || data.text) {
                callbacks.onToken?.(data.token || data.text || '');
              }
              break;
            case 'result': {
              const envelope = data.envelope || data.result || data.data;
              if (envelope) {
                callbacks.onResult?.(envelope);
              }
              break;
            }
            case 'error':
              callbacks.onError?.(
                new ChatbotApiError(
                  data.message || data.detail || 'WebSocket error from server',
                  undefined,
                  'WS_SERVER_ERROR',
                ),
              );
              break;
            case 'done':
              callbacks.onDone?.();
              break;
            default:
              break;
          }
        } catch {
          // Fallback if data is raw text token
          if (typeof event.data === 'string') {
            callbacks.onToken?.(event.data);
          }
        }
      };

      ws.onerror = () => {
        if (!isClosedManually) {
          callbacks.onError?.(
            new ChatbotApiError('WebSocket connection error', undefined, 'WS_CONNECTION_ERROR'),
          );
        }
      };

      ws.onclose = () => {
        if (!isClosedManually) {
          callbacks.onDone?.();
        }
      };
    } catch (err) {
      callbacks.onError?.(
        new ChatbotApiError(
          (err as Error).message || 'Failed to initialize WebSocket',
          undefined,
          'WS_INIT_FAILED',
        ),
      );
    }

    return () => {
      isClosedManually = true;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close();
      }
    };
  },
};

export default ecptChatbotService;
